// ─── In-app confirm/alert dialog ───
// USER PREFERENCE (Jun 2026): all confirmation prompts are DISABLED.
// `confirmDialog()` resolves to `true` immediately so the action runs
// without any popup. `alertDialog()` falls back to a non-blocking sonner
// toast for purely informational messages.
//
// The full modal implementation is preserved below for future use but is
// not invoked. To re-enable confirms in the future, restore the body of
// confirmDialog() to call _mount(...).

import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import { AlertTriangle, X, CheckCircle2, Info, ShieldAlert, HelpCircle } from "lucide-react";

const _TONES = {
  default: { color: "#1E3A5F", icon: HelpCircle, ring: "rgba(30,58,95,0.18)" },
  danger:  { color: "#E85D45", icon: ShieldAlert, ring: "rgba(232,93,69,0.22)" },
  warning: { color: "#F59E0B", icon: AlertTriangle, ring: "rgba(245,158,11,0.22)" },
  info:    { color: "#3B82F6", icon: Info, ring: "rgba(59,130,246,0.22)" },
  success: { color: "#15803D", icon: CheckCircle2, ring: "rgba(21,128,61,0.22)" },
};

const ConfirmModal = ({
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone,
  onResolve,
  alertOnly,
}) => {
  const t = _TONES[tone] || _TONES.default;
  const Icon = t.icon;
  const [closing, setClosing] = useState(false);

  const close = (val) => {
    if (closing) return;
    setClosing(true);
    // Allow exit animation before resolving
    setTimeout(() => onResolve(val), 120);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
      else if (e.key === "Enter" && !alertOnly) close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, []);

  return (
    <div
      className={`fixed inset-0 z-[10000] flex items-center justify-center px-4 ${closing ? "opacity-0" : "opacity-100"} transition-opacity duration-100`}
      style={{ background: "rgba(8,15,30,0.55)", backdropFilter: "blur(2px)" }}
      onClick={() => close(false)}
      dir="rtl"
      data-testid="app-confirm-overlay"
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden ${closing ? "scale-95" : "scale-100"} transition-transform duration-100`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div
          className="px-5 py-4 flex items-start gap-3"
          style={{ background: `linear-gradient(135deg, ${t.color} 0%, ${t.color}DD 100%)` }}
        >
          <div className="bg-white/20 rounded-full p-2 shrink-0">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-white text-base leading-snug" data-testid="app-confirm-title">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={() => close(false)}
            className="text-white/80 hover:text-white shrink-0"
            aria-label="إغلاق"
            data-testid="app-confirm-x"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* Body */}
        <div className="px-5 py-4 text-sm text-[#1E3A5F] whitespace-pre-line leading-relaxed" data-testid="app-confirm-body">
          {message}
        </div>
        {/* Footer */}
        <div className="px-5 pb-4 pt-1 flex gap-2 justify-end">
          {!alertOnly && (
            <button
              type="button"
              onClick={() => close(false)}
              data-testid="app-confirm-cancel"
              className="px-4 py-2 rounded-full text-sm font-bold bg-[#F3F6F4] text-[#5C6E64] hover:bg-[#E4EBE6] transition-colors"
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            autoFocus
            onClick={() => close(true)}
            data-testid="app-confirm-ok"
            className="px-5 py-2 rounded-full text-sm font-bold text-white shadow transition-transform hover:scale-[1.02]"
            style={{ background: t.color, boxShadow: `0 0 0 4px ${t.ring}` }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// Singleton mount that supports stacking by appending new roots.
const _mount = ({ alertOnly, title, message, confirmLabel, cancelLabel, tone }) => {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    host.setAttribute("data-app-confirm", "");
    document.body.appendChild(host);
    const root = createRoot(host);

    const cleanup = (val) => {
      try { root.unmount(); } catch { /* ignore */ }
      try { host.remove(); } catch { /* ignore */ }
      resolve(val);
    };

    root.render(
      <ConfirmModal
        alertOnly={alertOnly}
        title={title}
        message={message}
        confirmLabel={confirmLabel}
        cancelLabel={cancelLabel}
        tone={tone}
        onResolve={cleanup}
      />
    );
  });
};

// ─── Public API ───
// confirmDialog defaults to a SILENT no-op that immediately returns true so
// the caller proceeds without any popup (user disabled all confirms in Jun
// 2026). EXCEPTION: pass `{ force: true }` (or `tone: "danger"`) to
// actually show the blocking modal — used for high-stakes destructive
// actions like permanent invoice deletion that always need confirmation.
export const confirmDialog = (message, opts = {}) => {
  if (opts && (opts.force === true || opts.tone === "danger")) {
    return _mount({
      title: opts.title || "تأكيد العملية",
      message: typeof message === "string" ? message : "",
      confirmLabel: opts.confirmLabel || "تأكيد",
      cancelLabel: opts.cancelLabel || "إلغاء",
      tone: opts.tone || "danger",
    });
  }
  return Promise.resolve(true);
};

// alertDialog falls back to a non-blocking sonner toast — visible but
// requires no click, doesn't lock the page.
export const alertDialog = (message, opts = {}) => {
  if (!message) return Promise.resolve(true);
  try {
    const tone = opts.tone || "info";
    if (tone === "danger" || tone === "warning") toast.error(message);
    else if (tone === "success") toast.success(message);
    else toast.info(message);
  } catch { /* ignore */ }
  return Promise.resolve(true);
};
